import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  XCircle,
  ExternalLink,
  Star,
  Info,
  Calendar,
  History,
  AlertTriangle,
  Loader,
  DollarSign,
} from 'lucide-react';
import { CompanyProfile, PlansResponse, BillingTransaction, PlanBillingCycle } from '../../types';
import api from '../../services/api';
import { LoadingState } from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTitle } from '../../hooks/usePageTitle';

const KIWIFY_HELP_URL =
  'https://ajuda.kiwify.com.br/pt-br/article/como-cancelar-a-assinatura-do-produto-que-comprei-19d0my1/';

const SubscriptionPage: React.FC = () => {
  usePageTitle('Assinatura');
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [plans, setPlans] = useState<PlansResponse | null>(null);
  const [history, setHistory] = useState<BillingTransaction[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [billingCycle, setBillingCycle] = useState<Exclude<PlanBillingCycle, null>>('monthly');

  const isOwner = user?.role === 'owner';

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchPlans();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<CompanyProfile>('/company/profile');
      setProfile(response.data);
    } catch (err: any) {
      console.error('Error fetching subscription:', err);
      setError(err.response?.data?.error || 'Failed to load subscription information');
      toast.error('Erro ao carregar informações da assinatura');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      setLoadingPlans(true);
      const response = await api.get<PlansResponse>('/company/subscription/plans');
      setPlans(response.data);
    } catch (err: any) {
      console.error('Error fetching plans:', err);
      toast.error('Erro ao carregar planos disponíveis');
    } finally {
      setLoadingPlans(false);
    }
  };

  const fetchBillingHistory = async () => {
    if (history.length > 0) {
      setShowHistory(!showHistory);
      return;
    }

    try {
      setLoadingHistory(true);
      const response = await api.get<{ total: number; transactions: BillingTransaction[] }>(
        '/company/billing/history'
      );
      setHistory(response.data.transactions);
      setShowHistory(true);
    } catch (err: any) {
      console.error('Error fetching billing history:', err);
      toast.error('Erro ao carregar histórico de cobrança');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleUpgrade = async (planId: string) => {
    if (!isOwner) {
      toast.error('Apenas o proprietário pode fazer upgrade de plano');
      return;
    }

    try {
      setUpgrading(true);
      const response = await api.post('/company/subscription/upgrade', { plan_id: planId });
      toast.success(response.data.message);

      // Show next steps info
      if (response.data.next_steps) {
        toast(response.data.next_steps, {
          duration: 5000,
          icon: '💳',
        });
      }

      // Refresh data
      await fetchSubscriptionStatus();
      await fetchPlans();
    } catch (err: any) {
      console.error('Error upgrading plan:', err);
      toast.error(err.response?.data?.error || 'Erro ao solicitar upgrade');
    } finally {
      setUpgrading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!isOwner) {
      toast.error('Apenas o proprietário pode cancelar a assinatura');
      return;
    }

    try {
      setCanceling(true);
      const response = await api.post<{
        message: string;
        notice?: string;
        kiwify_help_url?: string;
      }>('/company/subscription/cancel', {
        reason: cancelReason
      });

      toast.success(response.data.message);

      if (response.data.notice) {
        toast(response.data.notice, {
          duration: 5000,
          icon: '⚠️',
        });
      }

      // Abre o artigo Kiwify automaticamente (próximo passo obrigatório).
      const helpUrl = response.data.kiwify_help_url || KIWIFY_HELP_URL;
      if (helpUrl) window.open(helpUrl, '_blank', 'noopener,noreferrer');

      setShowCancelConfirm(false);
      setCancelReason('');

      // Refresh data
      await fetchSubscriptionStatus();
    } catch (err: any) {
      console.error('Error canceling subscription:', err);
      toast.error(err.response?.data?.error || 'Erro ao cancelar assinatura');
    } finally {
      setCanceling(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const base = 'px-2 py-1 text-xs font-bold uppercase tracking-wider rounded-full border';
    switch (status) {
      case 'active':
        return <span className={`${base} bg-musgo/15 text-musgo border-musgo/30`}>Ativo</span>;
      case 'trial':
        return <span className={`${base} bg-tinta/8 text-tinta-soft border-tinta/15`}>Trial</span>;
      case 'cancelled':
        return <span className={`${base} bg-bordo/8 text-bordo border-bordo/30`}>Cancelado</span>;
      case 'expired':
        return <span className={`${base} bg-tinta/8 text-tinta-soft border-tinta/15`}>Expirado</span>;
      case 'past_due':
        return <span className={`${base} bg-mostarda/20 text-mostarda-dark border-mostarda/40`}>Pagamento Pendente</span>;
      default:
        return <span className={`${base} bg-tinta/8 text-tinta-soft border-tinta/15`}>{status}</span>;
    }
  };

  const getEventTypeBadge = (eventType: string) => {
    const base = 'px-2 py-1 text-xs font-bold uppercase tracking-wider rounded-full border';
    const badges: Record<string, { label: string; className: string }> = {
      order_approved: {
        label: 'Aprovado',
        className: 'bg-musgo/15 text-musgo border-musgo/30',
      },
      subscription_renewed: {
        label: 'Renovado',
        className: 'bg-musgo/15 text-musgo border-musgo/30',
      },
      order_refused: {
        label: 'Recusado',
        className: 'bg-bordo/8 text-bordo border-bordo/30',
      },
      order_rejected: {
        label: 'Rejeitado',
        className: 'bg-bordo/8 text-bordo border-bordo/30',
      },
      subscription_canceled: {
        label: 'Cancelado',
        className: 'bg-bordo/8 text-bordo border-bordo/30',
      },
      order_refunded: {
        label: 'Reembolsado',
        className: 'bg-mostarda/20 text-mostarda-dark border-mostarda/40',
      },
      subscription_late: {
        label: 'Atrasado',
        className: 'bg-mostarda/20 text-mostarda-dark border-mostarda/40',
      },
      order_chargeback: {
        label: 'Chargeback',
        className: 'bg-bordo-dark/10 text-bordo-dark border-bordo-dark/30',
      },
    };

    const badge = badges[eventType] || {
      label: eventType,
      className: 'bg-tinta/8 text-tinta-soft border-tinta/15',
    };
    return <span className={`${base} ${badge.className}`}>{badge.label}</span>;
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-bordo bg-bordo/10';
    if (percentage >= 70) return 'text-mostarda-dark bg-mostarda/15';
    return 'text-musgo bg-musgo/10';
  };

  const getUsageBarColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-bordo';
    if (percentage >= 70) return 'bg-mostarda';
    return 'bg-musgo';
  };

  if (loading) {
    return <LoadingState message="Carregando informações de assinatura..." />;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex gap-3 p-4 rounded-lg bg-bordo/8 border border-bordo/30">
          <AlertTriangle size={20} className="text-bordo flex-shrink-0 mt-0.5" />
          <p className="text-sm text-tinta">{error}</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="space-y-6">
        <div className="flex gap-3 p-4 rounded-lg bg-bordo/8 border border-bordo/30">
          <AlertTriangle size={20} className="text-bordo flex-shrink-0 mt-0.5" />
          <p className="text-sm text-tinta">Dados de assinatura não encontrados</p>
        </div>
      </div>
    );
  }

  const currentPlan = profile.plan;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-3xl font-black text-tinta leading-tight">
          Assinatura e <span className="italic text-bordo">planos</span>
        </h1>
        <p className="mt-2 text-tinta-soft">Gerencie sua assinatura e visualize os recursos disponíveis</p>
      </div>

      {/* Warning for non-owners */}
      {!isOwner && (
        <div className="flex gap-3 p-4 rounded-lg bg-mostarda/15 border border-mostarda/40">
          <AlertTriangle size={20} className="text-mostarda-dark flex-shrink-0 mt-0.5" />
          <p className="text-sm text-tinta">
            Apenas o proprietário da empresa pode fazer alterações na assinatura.
          </p>
        </div>
      )}

      {/* Current Subscription Status */}
      <div className="bg-white rounded-xl shadow-sm border border-tinta/10 p-6 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left - Plan info */}
          <div>
            <h2 className="font-display text-base font-bold text-tinta mb-3">
              Plano Atual
            </h2>
            <div className="flex items-center gap-3 mb-3">
              <h3 className="font-display text-3xl font-black text-bordo">
                {currentPlan.name}
              </h3>
              {profile.subscription.status && getStatusBadge(profile.subscription.status)}
            </div>
            {profile.subscription.ends_at && (
              <div className="flex items-center gap-2 text-tinta-soft">
                <Calendar size={16} />
                <span className="text-sm">
                  {profile.subscription.status === 'active' ? 'Renova em' : 'Expira em'}:{' '}
                  <span className="text-tinta font-medium">{formatDate(profile.subscription.ends_at)}</span>
                </span>
              </div>
            )}
            {currentPlan.pricing.monthly !== undefined && currentPlan.pricing.monthly > 0 && (
              <div className="mt-2 flex items-center gap-2 text-tinta-soft">
                <DollarSign size={16} />
                <span className="text-sm font-medium text-tinta">
                  {formatCurrency(currentPlan.pricing.monthly)} /mês
                </span>
              </div>
            )}
          </div>

          {/* Middle - Usage */}
          <div className="lg:col-span-2">
            <h2 className="font-display text-base font-bold text-tinta mb-3">
              Uso do Plano
            </h2>
            <div className="space-y-4">
              {/* Wheels */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-tinta">Rodas de Prêmios</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${getUsageColor(profile.usage.wheels.percentage)}`}>
                    {profile.usage.wheels.unlimited ? 'Ilimitado' : `${profile.usage.wheels.current} / ${profile.usage.wheels.limit}`}
                  </span>
                </div>
                {!profile.usage.wheels.unlimited && (
                  <div className="w-full bg-tinta/8 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${getUsageBarColor(profile.usage.wheels.percentage)}`}
                      style={{ width: `${Math.min(profile.usage.wheels.percentage, 100)}%` }}
                    />
                  </div>
                )}
              </div>

              {/* Products */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-tinta">Produtos</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${getUsageColor(profile.usage.products.percentage)}`}>
                    {profile.usage.products.unlimited ? 'Ilimitado' : `${profile.usage.products.current} / ${profile.usage.products.limit}`}
                  </span>
                </div>
                {!profile.usage.products.unlimited && (
                  <div className="w-full bg-tinta/8 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${getUsageBarColor(profile.usage.products.percentage)}`}
                      style={{ width: `${Math.min(profile.usage.products.percentage, 100)}%` }}
                    />
                  </div>
                )}
              </div>

              {/* Spins */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-tinta">Giros este Mês</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${getUsageColor(profile.usage.spins_month.percentage)}`}>
                    {profile.usage.spins_month.unlimited ? 'Ilimitado' : `${profile.usage.spins_month.current} / ${profile.usage.spins_month.limit}`}
                  </span>
                </div>
                {!profile.usage.spins_month.unlimited && (
                  <div className="w-full bg-tinta/8 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${getUsageBarColor(profile.usage.spins_month.percentage)}`}
                      style={{ width: `${Math.min(profile.usage.spins_month.percentage, 100)}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Billing History Button */}
        {isOwner && (
          <div className="mt-6 pt-6 border-t border-tinta/10">
            <button
              onClick={fetchBillingHistory}
              disabled={loadingHistory}
              className="flex items-center gap-2 text-bordo hover:text-bordo-dark font-bold transition-colors"
            >
              {loadingHistory ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Carregando...
                </>
              ) : (
                <>
                  <History size={16} />
                  {showHistory ? 'Ocultar' : 'Ver'} Histórico de Cobrança
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Billing History */}
      {showHistory && history.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-tinta/10 overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-tinta/10">
            <h2 className="font-display text-lg font-bold text-tinta">
              Histórico de Transações
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-creme-light border-b border-tinta/10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-tinta">Data</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-tinta">ID Pedido</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-tinta">Evento</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-tinta">Pagamento</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-tinta">Parcelas</th>
                </tr>
              </thead>
              <tbody>
                {history.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-creme-light/50 border-b border-tinta/8 last:border-0">
                    <td className="px-4 py-3 text-sm text-tinta">
                      {new Date(transaction.date).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-tinta">
                      {transaction.order_id}
                    </td>
                    <td className="px-4 py-3">
                      {getEventTypeBadge(transaction.event_type)}
                    </td>
                    <td className="px-4 py-3 text-sm text-tinta capitalize">
                      {transaction.payment_method.replace('_', ' ')}
                    </td>
                    <td className="px-4 py-3 text-sm text-tinta">
                      {transaction.installments}x
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Plan Comparison */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="font-display text-2xl font-black text-tinta">Planos Disponíveis</h2>

        {/* Toggle Mensal/Anual */}
        <div className="inline-flex bg-creme border border-tinta/15 rounded-lg p-1">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-1.5 rounded-md text-sm font-bold transition-colors ${
              billingCycle === 'monthly'
                ? 'bg-bordo text-creme shadow-sm'
                : 'text-tinta-soft hover:text-bordo'
            }`}
          >
            Mensal
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-4 py-1.5 rounded-md text-sm font-bold transition-colors ${
              billingCycle === 'yearly'
                ? 'bg-bordo text-creme shadow-sm'
                : 'text-tinta-soft hover:text-bordo'
            }`}
          >
            Anual
          </button>
        </div>
      </div>

      {loadingPlans ? (
        <div className="flex justify-center py-8">
          <Loader size={32} className="animate-spin text-bordo" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
          {plans?.available_plans
            ?.filter((plan) => plan.tier !== 'free' && plan.billing_cycle === billingCycle)
            .map((plan) => {
              const isCurrentPlan = plan.is_current;
              const isRecommended = plan.tier === 'pro';
              const price =
                billingCycle === 'monthly' ? plan.pricing.monthly : plan.pricing.yearly;
              const cycleLabel = billingCycle === 'monthly' ? '/mês' : '/ano';
              const checkoutUrl = plan.kiwify_checkout_url;

              return (
                <div
                  key={plan.id}
                  className={`bg-white rounded-xl shadow-sm border border-tinta/10 overflow-hidden relative ${
                    isCurrentPlan
                      ? 'ring-2 ring-musgo'
                      : isRecommended
                      ? 'ring-2 ring-bordo'
                      : ''
                  }`}
                >
                  {isRecommended && !isCurrentPlan && (
                    <div className="absolute top-4 right-4">
                      <div className="bg-bordo text-creme px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                        <Star size={14} />
                        Recomendado
                      </div>
                    </div>
                  )}

                  <div className="p-6">
                    <div className="text-center mb-6">
                      <h3 className="font-display text-2xl font-bold text-tinta mb-2">{plan.name}</h3>
                      <div className="flex items-baseline justify-center">
                        <span className="font-display text-4xl font-black text-tinta">
                          {formatCurrency(price || 0)}
                        </span>
                        <span className="text-tinta-soft ml-1">{cycleLabel}</span>
                      </div>
                    </div>

                    {isCurrentPlan && (
                      <div className="bg-musgo/10 border border-musgo/30 rounded-lg p-3 mb-4 flex items-center gap-2">
                        <CheckCircle size={18} className="text-musgo" />
                        <span className="text-sm font-bold text-musgo">Plano Atual</span>
                      </div>
                    )}

                    <div className="mb-6">
                      <p className="text-xs font-bold uppercase tracking-wider text-tinta mb-3">Recursos</p>
                      <ul className="space-y-2">
                        {plan?.features?.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm">
                            <CheckCircle
                              size={16}
                              className="text-musgo flex-shrink-0 mt-0.5"
                            />
                            <span className="text-tinta-soft">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="space-y-2">
                      {isCurrentPlan && isOwner ? (
                        <button
                          onClick={() => setShowCancelConfirm(true)}
                          className="w-full py-3 px-4 rounded-lg font-bold transition-colors flex items-center justify-center gap-2 bg-bordo/8 text-bordo border border-bordo/20 hover:bg-bordo/15"
                        >
                          <XCircle size={18} />
                          Cancelar Assinatura
                        </button>
                      ) : !isCurrentPlan && checkoutUrl ? (
                        <a
                          onClick={() => handleUpgrade(plan.id)}
                          href={checkoutUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`w-full py-3 px-4 rounded-lg font-bold transition-colors flex items-center justify-center gap-2 ${
                            isRecommended
                              ? 'bg-bordo text-creme hover:bg-bordo-dark'
                              : 'bg-tinta text-creme hover:bg-tinta-soft'
                          } ${upgrading ? 'opacity-60 pointer-events-none' : ''}`}
                        >
                          {upgrading ? (
                            <>
                              <Loader size={18} className="animate-spin" />
                              Processando...
                            </>
                          ) : (
                            <>
                              <ExternalLink size={18} />
                              Assinar {plan.name}
                            </>
                          )}
                        </a>
                      ) : !isCurrentPlan ? (
                        <button
                          disabled
                          className="w-full py-3 px-4 rounded-lg font-bold bg-tinta/10 text-tinta-soft border border-tinta/15 cursor-not-allowed"
                          title="Link de checkout indisponível. Contate o suporte."
                        >
                          Indisponível
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-tinta/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-tinta/10 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={24} className="text-bordo" />
              <h3 className="font-display text-xl font-bold text-tinta">Cancelar Assinatura</h3>
            </div>

            <p className="text-tinta-soft mb-4">
              Tem certeza que deseja cancelar sua assinatura <strong className="text-tinta font-bold">{currentPlan.name}</strong>?
              {profile.subscription.ends_at && (
                <>
                  {' '}
                  Você continuará com acesso até <strong className="text-tinta font-bold">{formatDate(profile.subscription.ends_at)}</strong>.
                  Após essa data, sua conta voltará para o plano Free.
                </>
              )}
            </p>

            {/* Passo obrigatório: cancelar na Kiwify */}
            <div className="mb-4 bg-mostarda/15 border border-mostarda/40 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle size={18} className="text-mostarda-dark flex-shrink-0 mt-0.5" />
                <div className="text-sm text-tinta">
                  <p className="font-bold mb-1">O cancelamento é feito na Kiwify</p>
                  <p className="mb-2">
                    Por questões de pagamento, o cancelamento precisa ser concluído na plataforma da Kiwify. Siga o passo a passo no artigo oficial:
                  </p>
                  <a
                    href={KIWIFY_HELP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-bordo hover:text-bordo-dark font-bold underline"
                  >
                    <ExternalLink size={14} />
                    Como cancelar minha assinatura na Kiwify
                  </a>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="reason" className="block text-xs font-bold uppercase tracking-wider text-tinta mb-1.5">
                Antes de ir, conte-nos o motivo (opcional)
              </label>
              <textarea
                id="reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                maxLength={500}
                className="w-full bg-white border border-tinta/15 rounded-lg px-3 py-2.5 text-sm text-tinta placeholder:text-tinta-soft/70 placeholder:italic focus:outline-none focus:ring-2 focus:ring-bordo/40 focus:border-bordo resize-y"
                placeholder="Conte-nos por que está cancelando..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelConfirm(false);
                  setCancelReason('');
                }}
                disabled={canceling}
                className="flex-1 px-4 py-2 border border-tinta/15 rounded-lg text-sm font-semibold text-tinta-soft hover:bg-creme-light hover:text-tinta transition-colors disabled:opacity-50"
              >
                Voltar
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={canceling}
                className="flex-1 px-4 py-2 bg-bordo text-creme rounded-lg hover:bg-bordo-dark transition-colors disabled:opacity-60 flex items-center justify-center gap-2 font-bold"
              >
                {canceling ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <ExternalLink size={16} />
                    Enviar motivo e abrir Kiwify
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Information Alert */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-tinta/5 border border-tinta/15">
        <Info size={20} className="text-tinta-soft flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-display font-bold text-tinta mb-2">Informações Importantes</h4>
          <ul className="text-sm text-tinta-soft space-y-1">
            <li>• Upgrades são processados automaticamente após confirmação do pagamento</li>
            <li>• Cancelamentos mantêm o acesso até o final do período já pago</li>
            <li>• Seu plano será renovado automaticamente na data de vencimento</li>
            <li>• Para dúvidas sobre cobrança, entre em contato com o suporte</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
